import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Star, ShoppingCart, ShieldCheck, Zap } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { formatINR } from "@/api/client";
import { useI18n } from "@/contexts/I18nContext";

const ProductCard = ({ product }) => {
  const { addItem, upsertItem } = useCart();
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const discount = product.mrp && product.price ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 0;
  const img = product.images?.[0] || "https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?w=600";

  const handleBuyNow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    upsertItem(product, 1);
    if (!user) {
      navigate(`/login?next=${encodeURIComponent("/checkout")}`);
    } else {
      navigate("/checkout");
    }
  };

  return (
    <div className="card-fah overflow-hidden hover-lift group" data-testid={`product-card-${product.id}`}>
      <Link to={`/product/${product.id}`} className="block">
        <div className="aspect-[4/5] bg-muted/50 overflow-hidden relative">
          <img src={img} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
          {discount > 0 && (
            <span className="absolute top-2 left-2 bg-secondary text-secondary-foreground text-[10px] font-bold px-2 py-1 rounded-md uppercase tracking-wide">
              {discount}% {t("OFF")}
            </span>
          )}
          <span className="absolute top-2 right-2 bg-white/95 backdrop-blur text-[10px] font-semibold text-primary px-2 py-1 rounded-md flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> {t("Verified")}
          </span>
        </div>
      </Link>
      <div className="p-3 sm:p-4">
        <Link to={`/product/${product.id}`}>
          <div className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">{product.brand}</div>
          <h3 className="text-sm font-semibold leading-tight line-clamp-2 min-h-[2.5rem] hover:text-primary" data-testid={`product-name-${product.id}`}>
            {t(product.name)}
          </h3>
        </Link>
        <div className="mt-2 flex items-center gap-1 text-xs">
          <div className="flex items-center gap-0.5 bg-primary text-primary-foreground px-1.5 py-0.5 rounded">
            <span className="font-semibold">{product.rating}</span>
            <Star className="w-3 h-3 fill-current" />
          </div>
          <span className="text-muted-foreground">({product.num_reviews})</span>
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-lg font-bold text-foreground">{formatINR(product.price)}</span>
          {product.mrp > product.price && <span className="text-xs text-muted-foreground line-through">{formatINR(product.mrp)}</span>}
        </div>
        <div className="mt-1 text-[11px] text-muted-foreground truncate">{t("by")} {product.seller_name} · {t(product.unit)}</div>
        <div className="mt-3 grid grid-cols-2 gap-1.5">
          <button
            onClick={() => addItem(product, 1)}
            className="bg-secondary hover:opacity-90 text-secondary-foreground font-semibold text-xs sm:text-sm rounded-lg py-2 flex items-center justify-center gap-1 transition"
            data-testid={`add-cart-${product.id}`}
          >
            <ShoppingCart className="w-4 h-4" /> <span className="hidden sm:inline">{t("Add to Cart")}</span><span className="sm:hidden">{t("Cart")}</span>
          </button>
          <button
            onClick={handleBuyNow}
            className="text-white font-bold text-xs sm:text-sm rounded-lg py-2 flex items-center justify-center gap-1 transition hover:opacity-90"
            style={{ backgroundColor: "#16A34A" }}
            data-testid={`buy-now-${product.id}`}
          >
            <Zap className="w-4 h-4 fill-white" /> {t("Buy Now")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
